import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { Truck, CurrencyDollar, Wrench, ShieldCheck } from "@phosphor-icons/react";

const icons = [CurrencyDollar, Wrench, Truck, ShieldCheck];

export default function PricingSection() {
  const { t } = useLanguage();

  return (
    <section className="py-24 md:py-32 bg-[#050505]" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-[#FFD700] mb-4 font-light">{t.pricing.overline}</p>
          <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-5xl font-light text-white tracking-tight">{t.pricing.title}</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {t.pricing.packages.map((pkg, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              className={`p-8 border ${i === 1 ? "bg-[#1A1A1A] border-[#FFD700]/40" : "bg-[#121212] border-white/10"}`}
              data-testid={`pricing-card-${i}`}
            >
              {i === 1 && (
                <span className="inline-block px-3 py-1 text-xs uppercase tracking-wider bg-[#FFD700] text-black mb-4">{t.pricing.popular}</span>
              )}
              <h3 className="font-['Cormorant_Garamond'] text-2xl text-white mb-2">{pkg.name}</h3>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="font-['Cormorant_Garamond'] text-4xl text-[#FFD700]">{pkg.price}</span>
                <span className="text-zinc-500 text-sm">{pkg.unit}</span>
              </div>
              <p className="text-zinc-400 text-sm font-light mb-6 leading-relaxed">{pkg.description}</p>
              <ul className="space-y-3 mb-8">
                {pkg.features.map((feature, fi) => (
                  <li key={fi} className="flex items-start gap-2 text-sm text-zinc-300 font-light">
                    <span className="text-[#FFD700] mt-0.5">&#10003;</span>
                    {feature}
                  </li>
                ))}
              </ul>
              <a
                href="#cotizacion"
                className={`block text-center py-3 text-sm uppercase tracking-wider transition-all duration-300 ${
                  i === 1
                    ? "bg-[#FFD700] text-black hover:bg-[#E5C100]"
                    : "border border-white/20 text-white hover:border-[#FFD700] hover:text-[#FFD700]"
                }`}
                data-testid={`pricing-cta-${i}`}
              >
                {t.pricing.cta}
              </a>
            </motion.div>
          ))}
        </div>

        {/* Benefits row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {t.pricing.benefits.map((benefit, i) => {
            const Icon = icons[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="flex items-start gap-3 p-4 border border-white/5"
                data-testid={`benefit-${i}`}
              >
                <Icon size={22} weight="duotone" className="text-[#FFD700] flex-shrink-0 mt-0.5" />
                <p className="text-zinc-400 text-xs font-light leading-relaxed">{benefit}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
